import React, { useMemo } from "react";
import { FONT } from "../shared";

// ─────────────────────────────────────────────────────────────────────────
// VellaOrb — the signature living visual of Vella. A rich-blue liquid sphere
// (Amplifx brand blue: deep royal → sky) that morphs through the generation
// lifecycle:
//
//   idle       → smooth breathing blue sphere with a centered label
//   generating → rounded blue box: electric border, letter-by-letter task
//                text, sweeping shimmer bar
//   result     → browser-chrome preview card (dots + URL + Open + faux site)
//
// All transitions are pure CSS (≈380ms ease-out morphs on width / height /
// border-radius). Self-contained: every style lives in the <style> block
// below — no external CSS files.
//
// Props:
//   state   "idle" | "generating" | "result"        (default "idle")
//   label   string  — centered in idle                (default "Vella")
//   task    string  — shown while generating          (default "Generating")
//   result  { kind, title, url?, image? }             — shown in result
//   onClick fn       — fires on click in result state
//   size    number  — resting diameter in px          (default 200)
// ─────────────────────────────────────────────────────────────────────────

const CSS = `
@keyframes vellaOrbBreathe { 0%,100% { transform: scale(1); } 50% { transform: scale(1.035); } }
@keyframes vellaOrbLiquid  { 0% { transform: translate(0,0) rotate(0deg); } 50% { transform: translate(-6%,4%) rotate(180deg); } 100% { transform: translate(0,0) rotate(360deg); } }
@keyframes vellaOrbBorder  { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
@keyframes vellaOrbBorderPulse { 0%,100% { opacity: 0.85; } 50% { opacity: 1; } }
@keyframes vellaOrbShimmer { 0% { transform: translateX(-130%); } 100% { transform: translateX(230%); } }
@keyframes vellaOrbLetter  { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
@keyframes vellaOrbResultIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }

.vella-orb-root {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${FONT};
  /* width/height + border-radius animate between states */
  transition: width 380ms cubic-bezier(0.22,1,0.36,1),
              height 380ms cubic-bezier(0.22,1,0.36,1),
              border-radius 380ms cubic-bezier(0.22,1,0.36,1),
              box-shadow 380ms ease-out,
              background 380ms ease-out;
  will-change: width, height, border-radius;
}

/* Rich Amplifx blue — deep royal core, sky-blue highlight. Shared by the
   idle sphere and the generating box so the morph keeps one material. */
.vella-orb-root[data-state="idle"],
.vella-orb-root[data-state="generating"] {
  background:
    radial-gradient(circle at 34% 28%, #dbeafe 0%, #bfdbfe 20%, #93c5fd 48%, #60a5fa 76%, #3b82f6 100%);
  overflow: hidden;
}

/* ── IDLE: living gradient sphere rendered as a button ── */
.vella-orb-root[data-state="idle"] {
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  border: none;
  -webkit-tap-highlight-color: transparent;
  animation: vellaOrbBreathe 7s ease-in-out infinite;
  box-shadow:
    inset 0 0 0 1px rgba(191,219,254,0.45),
    inset 8px 10px 44px rgba(219,234,254,0.42),
    inset -12px -16px 60px rgba(59,130,246,0.28),
    0 20px 54px -18px rgba(96,165,250,0.40),
    0 0 62px 4px rgba(147,197,253,0.34);
  transition: box-shadow 220ms ease-out;
}
/* Hover: the blue glow intensifies (softly). */
.vella-orb-root[data-state="idle"]:hover {
  box-shadow:
    inset 0 0 0 1px rgba(191,219,254,0.55),
    inset 8px 10px 44px rgba(219,234,254,0.50),
    inset -12px -16px 60px rgba(59,130,246,0.28),
    0 24px 64px -18px rgba(96,165,250,0.50),
    0 0 82px 6px rgba(147,197,253,0.46);
}
/* Visible focus ring (ocean blue, offset). */
.vella-orb-root[data-state="idle"]:focus-visible {
  outline: 3px solid #275c73;
  outline-offset: 4px;
}
/* Slow-drifting inner light keeps the sphere feeling liquid and alive. */
.vella-orb-root[data-state="idle"] .vella-orb-liquid {
  position: absolute; inset: -25%; border-radius: 50%; pointer-events: none;
  background:
    radial-gradient(circle at 30% 28%, rgba(219,234,254,0.65) 0%, transparent 42%),
    radial-gradient(circle at 72% 76%, rgba(96,165,250,0.45) 0%, transparent 46%);
  mix-blend-mode: screen;
  animation: vellaOrbLiquid 14s ease-in-out infinite;
}
.vella-orb-root[data-state="idle"] .vella-orb-gloss {
  position: absolute; inset: 0; border-radius: 50%; pointer-events: none;
  background: radial-gradient(circle at 36% 26%,
      rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.14) 24%, transparent 48%);
}
@media (prefers-reduced-motion: reduce) {
  .vella-orb-root[data-state="idle"] { animation: none !important; }
  .vella-orb-root[data-state="idle"] .vella-orb-liquid { animation: none !important; }
}
.vella-orb-label {
  position: relative;
  z-index: 2;
  color: #fff;
  font-weight: 600;
  font-size: 28px;
  letter-spacing: -0.01em;
  text-shadow: 0 1px 8px rgba(8,30,90,0.50), 0 0 22px rgba(56,189,248,0.55);
  user-select: none;
}

/* ── GENERATING: contained-energy blue box (soft) ── */
.vella-orb-root[data-state="generating"] {
  border-radius: 24px;
  box-shadow:
    0 22px 58px -18px rgba(96,165,250,0.45),
    0 0 46px 2px rgba(147,197,253,0.34);
}
.vella-orb-root[data-state="generating"] .vella-orb-liquid {
  position: absolute; inset: -20%; pointer-events: none;
  background:
    radial-gradient(circle at 28% 24%, rgba(186,230,253,0.55) 0%, transparent 44%),
    radial-gradient(circle at 76% 80%, rgba(29,78,216,0.55) 0%, transparent 48%);
  mix-blend-mode: screen;
  animation: vellaOrbLiquid 12s ease-in-out infinite;
}
/* Animated electric-blue gradient border (sits just inside the box edge). */
.vella-orb-border {
  position: absolute; inset: 0; border-radius: 24px; padding: 2px;
  background: linear-gradient(90deg,
      #bae6fd, #7dd3fc, #38bdf8, #60a5fa, #93c5fd, #bae6fd);
  background-size: 200% 100%;
  animation: vellaOrbBorder 2.4s linear infinite, vellaOrbBorderPulse 1.8s ease-in-out infinite;
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          mask-composite: exclude;
  pointer-events: none;
}
.vella-orb-gen-inner {
  position: relative; z-index: 2;
  display: flex; flex-direction: column; align-items: center; gap: 13px;
  padding: 0 22px;
}
.vella-orb-task {
  display: flex; flex-wrap: wrap; justify-content: center;
  color: #ffffff; font-size: 16px; font-weight: 600; letter-spacing: 0.01em;
  text-shadow: 0 1px 6px rgba(8,30,90,0.40);
}
.vella-orb-task span {
  display: inline-block; white-space: pre;
  opacity: 0;
  animation: vellaOrbLetter 420ms ease-out forwards;
}
.vella-orb-bar {
  position: relative; width: 140px; height: 6px; border-radius: 999px;
  background: rgba(255,255,255,0.28); overflow: hidden;
}
.vella-orb-bar::after {
  content: ''; position: absolute; top: 0; bottom: 0; width: 45%;
  border-radius: 999px;
  background: linear-gradient(90deg, transparent, #ffffff 45%, #eaf6ff 60%, transparent);
  animation: vellaOrbShimmer 1.5s ease-in-out infinite;
}

/* ── RESULT: browser-chrome preview card ── */
.vella-orb-root[data-state="result"] {
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 18px 50px -16px rgba(20,40,90,0.45), 0 0 0 1px rgba(20,40,90,0.06);
  overflow: hidden;
  cursor: pointer;
  animation: vellaOrbResultIn 360ms cubic-bezier(0.22,1,0.36,1);
  transition: transform 220ms ease-out, box-shadow 220ms ease-out,
              width 380ms cubic-bezier(0.22,1,0.36,1),
              height 380ms cubic-bezier(0.22,1,0.36,1),
              border-radius 380ms cubic-bezier(0.22,1,0.36,1);
}
.vella-orb-root[data-state="result"]:hover {
  transform: translateY(-2px);
  box-shadow: 0 26px 64px -16px rgba(20,40,90,0.55), 0 0 0 1px rgba(20,40,90,0.08);
}
.vella-orb-card { position: absolute; inset: 0; display: flex; flex-direction: column; }
.vella-orb-chrome {
  flex: 0 0 auto; height: 32px; display: flex; align-items: center; gap: 9px;
  padding: 0 12px; background: #f1f4f9; border-bottom: 1px solid #e2e8f2;
}
.vella-orb-dots { display: flex; gap: 6px; flex: 0 0 auto; }
.vella-orb-dot { width: 9px; height: 9px; border-radius: 50%; }
.vella-orb-urlbar {
  flex: 1; min-width: 0; height: 19px; border-radius: 999px; background: #fff;
  border: 1px solid #e2e8f2; display: flex; align-items: center;
  padding: 0 10px; font-size: 10px; font-weight: 500; color: #64748b;
  overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
}
.vella-orb-open {
  flex: 0 0 auto; font-size: 11px; font-weight: 600; color: #2563eb;
  letter-spacing: -0.01em;
}
.vella-orb-body { flex: 1; position: relative; overflow: hidden; background: #fff; }
.vella-orb-body img { width: 100%; height: 100%; object-fit: cover; display: block; }

/* Faux-site preview (CSS only) — clean white page, blue accent, a heading
   and two buttons, mirroring a real landing page. */
.vella-orb-faux {
  position: absolute; inset: 0; display: flex; flex-direction: column;
  align-items: flex-start; justify-content: center; gap: 13px;
  padding: 0 26px;
  background:
    radial-gradient(120% 90% at 85% -10%, rgba(56,189,248,0.16) 0%, transparent 55%),
    #ffffff;
}
.vella-orb-faux-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 0.10em; text-transform: uppercase; color: #2563eb; }
.vella-orb-faux-title { font-size: 19px; font-weight: 800; letter-spacing: -0.02em; color: #0f1f44; line-height: 1.15; }
.vella-orb-faux-sub { font-size: 11px; font-weight: 500; color: #64748b; max-width: 80%; line-height: 1.3; }
.vella-orb-faux-btns { display: flex; gap: 9px; margin-top: 4px; }
.vella-orb-btn-primary {
  font-size: 11px; font-weight: 600; color: #fff; padding: 6px 13px; border-radius: 8px;
  background: linear-gradient(135deg, #38bdf8 0%, #2563eb 55%, #1d4ed8 100%);
  box-shadow: 0 6px 16px -8px rgba(37,99,235,0.7);
}
.vella-orb-btn-secondary {
  font-size: 11px; font-weight: 600; color: #2563eb; padding: 6px 13px; border-radius: 8px;
  background: #fff; border: 1px solid #c7d6f0;
}
`;

export function VellaOrb({
  state = "idle",
  label = "Vella",
  task = "Generating",
  result = null,
  onClick,
  size = 200,
}) {
  // Generating box is wider than the resting sphere (~1.6x); result keeps a
  // browser-card aspect. Heights derive from `size` so callers scale cleanly.
  const dims =
    state === "generating"
      ? { width: Math.round(size * 1.6), height: Math.round(size * 0.9) }
      : state === "result"
      ? { width: Math.round(size * 1.7), height: Math.round(size * 1.18) }
      : { width: size, height: size };

  // Letter-by-letter task text — staggered fade-in.
  const letters = useMemo(() => Array.from(task), [task]);

  // Idle and result are interactive; generating is not.
  const interactive = state === "idle" || state === "result";

  const handleClick = () => {
    if (state === "result") {
      if (onClick) onClick(result);
      else if (result?.url) window.open(result.url, "_blank", "noopener");
    } else if (state === "idle") {
      if (onClick) onClick();
    }
  };

  const handleKeyDown = (e) => {
    if (!interactive) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  const urlText = result?.url || "vella.site";
  const title = result?.title || "Your landing page";

  return (
    <>
      <style>{CSS}</style>
      <div
        className="vella-orb-root"
        data-state={state}
        role={interactive ? "button" : "img"}
        tabIndex={interactive ? 0 : undefined}
        aria-label={
          state === "idle" ? label : state === "generating" ? task : title
        }
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        style={{ width: dims.width, height: dims.height }}
      >
        {state === "idle" && (
          <>
            <span className="vella-orb-liquid" />
            <span className="vella-orb-gloss" />
            <span className="vella-orb-label">{label}</span>
          </>
        )}

        {state === "generating" && (
          <>
            <span className="vella-orb-liquid" />
            <span className="vella-orb-border" />
            <div className="vella-orb-gen-inner">
              <div className="vella-orb-task" aria-hidden="true">
                {letters.map((ch, i) => (
                  <span key={i} style={{ animationDelay: `${i * 45}ms` }}>
                    {ch}
                  </span>
                ))}
              </div>
              <div className="vella-orb-bar" />
            </div>
          </>
        )}

        {state === "result" && (
          <div className="vella-orb-card">
            <div className="vella-orb-chrome">
              <div className="vella-orb-dots">
                <span className="vella-orb-dot" style={{ background: "#ff5f57" }} />
                <span className="vella-orb-dot" style={{ background: "#febc2e" }} />
                <span className="vella-orb-dot" style={{ background: "#28c840" }} />
              </div>
              <div className="vella-orb-urlbar">{urlText}</div>
              <span className="vella-orb-open">Open</span>
            </div>
            <div className="vella-orb-body">
              {result?.image ? (
                <img src={result.image} alt={title} />
              ) : (
                <div className="vella-orb-faux">
                  <div className="vella-orb-faux-eyebrow">
                    {result?.kind === "website" ? "Website" : "Preview"}
                  </div>
                  <div className="vella-orb-faux-title">{title}</div>
                  <div className="vella-orb-faux-sub">
                    A clean, ready-to-publish page built by Vella.
                  </div>
                  <div className="vella-orb-faux-btns">
                    <span className="vella-orb-btn-primary">Get started</span>
                    <span className="vella-orb-btn-secondary">Learn more</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default VellaOrb;
